/**
 * Defines methods for "shipping" components between windows/UIContainers in support of docking and undocking
 *
 * @module MW/uiframework/uicontainer/DockingManager
 * @copyright Copyright 2015-2017 The MathWorks, Inc.
 */
define(["dojo/_base/lang",
        "dojo/Deferred",
        "dojo/on",
        "dojo/when",
        "mw-remote/Remote",
        "MW/uiframework/CssLoader",
        "MW/uiframework/uicontainer/ChildProperties",
        "MW/uiframework/uicontainer/ChildWindowManager",
        "MW/uiframework/uicontainer/DocumentTypeProperties"
], function (lang, Deferred, on, when, Remote, CssLoader, ChildProperties, ChildWindowManager, DocumentTypeProperties) {

    var windowNameForType = {};
    var dockUI = {};
    var uiFactoryPath = {};

    var SINGLE_DOCUMENT_CONTENT = "_singleDocument_";
    var CHILD_UI_CONTENT        = "_childUI_";

    // Save in window so that child windows can reference
    window.DockingManager = {

        _tabGroupFactories: [],

        createPackingList: function (container, parts) {
            var properties, tag, factory;
            var packingList = {
                cssFiles : container.cssFiles || [],
                types: [],
                tabGroups: [],
                panels: [],
                documents: [],
                remote: {
                    routingHost: Remote.getWorkerRoutingHost(),
                    routingKey: Remote.getWorkerRoutingKey(),
                    computeSessionId: Remote.getComputeSessionId()
                }
            };
            if (parts && parts.types) {
                packingList.types = parts.types;
                parts.types.forEach(function (type) {
                    properties = container._getPropertiesForType(type);
                    for (tag in properties.tabGroupFactories) {
                        factory = properties.tabGroupFactories[tag];
                        packingList.tabGroups.push({
                            tag: tag,
                            type: type,
                            state: factory.getState(),
                            factoryPath: factory.modulePath
                        })
                    }
                });
            }
            if (parts && parts.panels) {
                parts.panels.forEach(function (panelInfo) {
                    packingList.panels.push({
                        region: panelInfo.properties.region,
                        state: panelInfo.factory.getState(panelInfo.widget, panelInfo.properties),
                        factoryPath: panelInfo.factory.modulePath
                    });
                });
            }
            if (parts && parts.documents) {
                parts.documents.forEach(function (documentInfo) {
                    packingList.documents.push({
                        isDocked: !documentInfo.properties.isDocked,
                        isSelected: documentInfo.properties.isSelected,
                        properties: documentInfo.properties === documentInfo.widget ? null : documentInfo.properties,
                        state: documentInfo.widget ? (documentInfo.factory.getState(documentInfo.widget, documentInfo.properties)) : documentInfo.state,
                        factoryPath: documentInfo.factory.modulePath
                    });
                });
            }
            return packingList;
        },

        savePackingList: function (packingList) {
            // Pick a random key to save under
            var key = this._randomString(32);
            // Simply store under the DockingManager object for the child to access
            key = "_" + key;
            if (this._isUndocked) {
                window.opener.DockingManager[key] = packingList;
            } else {
                window.DockingManager[key] = packingList;
            }
            return key;
        },

        retrievePackingList: function (key) {
            return this._isUndocked ? window.opener.DockingManager[key] : window.DockingManager[key];
        },

        discardPackingList: function (key) {
            var win = this._isUndocked ? window.opener : window;
            delete win.DockingManager[key];
        },

        /**
         * Stores a reference to the container inhabiting this window
         */
        setContainer: function (container) {
            this._container = container;
        },

        getContainer: function () {
            return this._container;
        },

        setIsUndockedWindow: function (value) {
            this._isUndocked = value;
        },

        isUndockedWindow: function () {
            return !!this._isUndocked;
        },

        spawnContainer: function (parent, page, type, packingList, bounds) {
            var that = this;
            var child = ChildWindowManager.openSynchronously({
                page: page || "/ui/webwidgets/src/js/MW/uiframework/uicontainer/undocked.html",
                initialData: packingList,
                closeHandler: function (childInfo) {

                    var container = childInfo.childWindow.DockingManager._container;
                    if (container) {
                        container.destroyRecursive();
                    } else {
                        // The window was closed before container creation was completed.
                        that._closePackedDocuments(packingList);
                    }
                    childInfo.childWindow.DockingManager._tabGroupFactories.forEach(function(factory) {
                        factory.destroyWidget();
                    });

                    var contentType = container.contentType;
                    if (contentType && contentType !== "_singleDocument_" && windowNameForType[contentType]) {
                        delete windowNameForType[contentType];
                    }
                }
            });
            if (!child) {
                throw Error("Unable to create child window");
            }
            if (type && type !== CHILD_UI_CONTENT && type !== SINGLE_DOCUMENT_CONTENT) {
                this._setWindowForType(type, child);
            }
            return child;
        },

        returnToOriginalContainer: function (undockedContainer, packingList) {
            // Runs in context of child window
            window.opener.DockingManager.dockChild(packingList);
        },

        sendToUndockedContainer: function (parentContainer, page, type, packingList) {
            // Runs in context of parent window
            if (!this._windowForTypeExists(type)) {
                this.spawnContainer(parentContainer, page, type, packingList);
            } else {
                var childWindow = this._getWindowForType(type);
                childWindow.DockingManager.addToContainer(childWindow.DockingManager._container, packingList);
                childWindow.focus();
            }
        },

        buildContainer: function (UIContainer, packingList) {
            var that = this;

            if (packingList.cssFiles) {
                CssLoader.loadFiles(packingList.cssFiles);
            }

            var deferred = new Deferred();
            if (packingList.containerFactoryPath) {
                // A container factory has been provided.  Use it.
                require([packingList.containerFactoryPath], function(ContainerFactory) {
                    var container = new ContainerFactory().createContainer({
                        parentContainer: packingList.parent,
                        contentType: CHILD_UI_CONTENT,
                        isDocked: packingList.isDocked
                    });
                    dockUI[container.id] = container.isDocked;
                    uiFactoryPath[container.id] = packingList.containerFactoryPath;
                    deferred.resolve(container);
                });
            } else {
                // Create a generic UIContainer an populate it.
                var needsToolstrip = that.requiresToolstrip(packingList);
                var container = new UIContainer(packingList.contentType, {
                    title: packingList.title,
                    reserveDocumentSpace: true,
                    showSingleDocumentTab: packingList.contentType !== "_singleDocument_",
                    offerDocumentMaximizeButton: false,
                    parentContainer: packingList.parent,
                    enableQABCustomization: packingList.parent.enableQABCustomization,
                    contentType: packingList.contentType,
                    hasToolstrip: needsToolstrip,
                    hasMnemonics: packingList.parent.hasMnemonics && needsToolstrip,
                    isDockable: that.isDockable(packingList),
                    product: packingList.parent.product,
                    scope: packingList.parent.scope
                });
                that.addToContainer(container, packingList);
                deferred.resolve(container);
            }
            return deferred.promise;
        },

        isDockable: function (packingList) {
            var isDockable = true;
            if (packingList.contentType === SINGLE_DOCUMENT_CONTENT) {
                if (packingList.documents && packingList.documents.length == 1) {
                    isDockable = packingList.documents[0].properties.isDockable !== false;
                }
            } else if (packingList.contentType !== CHILD_UI_CONTENT) {
                if (packingList.types && packingList.types.length == 1) {
                    isDockable = packingList.parent.getDocumentTypeProperties(packingList.types[0]).isDockable !== false;
                }
            }
            return isDockable;
        },

        requiresToolstrip: function (packingList) {
            return packingList.tabGroups && packingList.tabGroups.length > 0;
        },

        addToContainer: function (container, packingList) {
            // Need to add the document types before restoring the document container state
            var restoringState;
            this._addTypesToContainer(container, packingList);
            delete packingList.types;

            if (packingList.documentContainerState) {
                if (!container._documentContainer) {
                    container._documentContainerState = packingList.documentContainerState;
                } else if (container._documentContainer.isEmpty()) {
                    restoringState = true;
                    container._documentContainer.setState(packingList.documentContainerState);
                }
            }
            var deferred = this._addTabGroupsToContainer(container, packingList);
            this._addPanelsToContainer(container, packingList);
            var that = this;
            deferred.then(function() {
                that._addDocumentsToContainer(container, packingList, restoringState).then(function () {
                    if (restoringState) {
                        container._documentContainer.endStateRestore();
                    }
                });
            });
        },

        _closePackedDocuments: function (packingList) {
            if (packingList.documents) {
                packingList.documents.forEach(function (documentInfo) {
                    documentInfo.properties.set(ChildProperties.IS_OPEN, false);
                });
            }
        },

        dockChild: function (packingList, parentContainer) {
            // Runs in context of parent window
            parentContainer = parentContainer || this._container;
            this.addToContainer(parentContainer, packingList);
        },

        shouldDockUI: function (id) {
            return !!dockUI[id];
        },

        getUIFactoryPath: function (id) {
            return uiFactoryPath[id];
        },

        _addTypesToContainer: function (container, packingList) {
            var properties;
            if (packingList.types) {
                packingList.types.forEach(function (type) {
                    properties = container._getPropertiesForType(type);
                    if (packingList.contentType === type) {
                        properties.set(DocumentTypeProperties.IS_DOCKED, !container.parentContainer);
                    }
                    container._addDocumentType(type);
                    /* TODO: Find a another way to handle this.
                     if (packingList.contentType === "_singleDocument_") {
                     delete container._getPropertiesForType(type).plusActions;
                     }
                     */
                });
            }
            if (!container.parentContainer && packingList.contentType !== "_singleDocument_") {
                container._getPropertiesForType(packingList.contentType).set("isDocked", true);
            }
        },

        _addTabGroupsToContainer: function (container, packingList) {
            var that = this;
            var deferred = new Deferred();
            if (packingList.tabGroups && (packingList.tabGroups.length > 0)) {
                packingList.tabGroups.forEach(function(tabGroupInfo) {
                    if (!container.uiBuilder.tagToId(tabGroupInfo.tag)) {
                        require([tabGroupInfo.factoryPath], function(Factory) {
                            var factory = new Factory();
                            that._tabGroupFactories.push(factory);
                            factory.createWidget(container, tabGroupInfo.state, container._getPropertiesForType(tabGroupInfo.type));
                            deferred.resolve();
                        });
                    } else {
                        deferred.resolve();
                    }
                });
            } else {
                deferred.resolve();
            }
            return deferred.promise;
        },

        _addPanelsToContainer: function (container, packingList) {
            if (packingList.panels && (packingList.panels.length > 0)) {
                packingList.panels.forEach(function (panelInfo) {
                    require([panelInfo.factoryPath], function(Factory) {
                        var factory = new Factory();
                        var properties = factory.createProperties(container, panelInfo.state);
                        properties.region = panelInfo.region;
                        container.addPanel(null, properties, factory, panelInfo.state);
                    });
                });
            }
        },

        _addDocumentsToContainer: function (container, packingList, restoringState) {
            var deferred = new Deferred();
            var count = 0;
            if (packingList.documents && (packingList.documents.length > 0)) {
                packingList.documents.forEach(function (documentInfo) {
                    require([documentInfo.factoryPath], function(Factory) {
                        var factory = new Factory();
                        documentInfo.properties.region = documentInfo.region;
                        documentInfo.properties.isDocked = documentInfo.isDocked;
                        CssLoader.loadFiles(factory.getCssFiles());
                        when(factory.createWidget(container, documentInfo.state, documentInfo.properties), function (widget) {
                            if (!restoringState) {
                                delete documentInfo.properties.tile;
                                delete documentInfo.properties.index;
                            }
                            container.addDocument(widget, documentInfo.properties);
                            // Bring undock window forward upon selection.
                            container.own(documentInfo.properties.watch(ChildProperties.IS_SELECTED, function () {
                                if (!documentInfo.properties.isDocked) {
                                    window.focus();  // Doesn't seem to work as advertised
                                }
                            }));
                            count++;
                            if (count === packingList.documents.length) {
                                deferred.resolve();
                            }
                        });
                    });
                });
            } else {
                deferred.resolve();
            }
            return deferred.promise;
        },

        _setWindowForType: function (type, childWindow) {
            // Runs in context of parent window
            windowNameForType[type] = childWindow.name;
        },

        _getWindowForType: function (type) {
            var windowName = windowNameForType[type];
            return windowName ? ChildWindowManager.getWindowByName(windowName) : null;
        },

        _windowForTypeExists: function (type) {
            // Runs in context of parent window
            return !!this._getWindowForType(type);
        }
    };

    window.DockingManager.SINGLE_DOCUMENT_CONTENT = SINGLE_DOCUMENT_CONTENT;
    window.DockingManager.CHILD_UI_CONTENT        = CHILD_UI_CONTENT;

    return window.DockingManager;
});